import { useCallback, useEffect, useState } from 'react';
import { listAllEmployees, peopleGateway } from '../../people';
import type { EmployeeResponse } from '../../people/types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import type { EscoHrWorkforceSummaryResponse } from '../types';

/** Loads something once and again on demand. A failure is kept as an error, never turned into an empty result. */
export function useEscoHrLoad<T>(enabled: boolean, load: (signal: AbortSignal) => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    (async () => {
      try {
        const next = await load(controller.signal);
        if (controller.signal.aborted) return;
        setData(next);
        setError(null);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(friendlyErrorMessage(err, 'Could not load this from escoHR.'));
      }
    })();
    return () => controller.abort();
  }, [enabled, load, tick]);

  const reload = useCallback(() => { setError(null); setData(null); setTick((n) => n + 1); }, []);
  return { data, error, reload, loading: enabled && data === null && error === null };
}

/** Names for the employees the records refer to. Failing to load them never hides the records: an id with no name shows generically. */
export function useEmployeeNames(enabled: boolean) {
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const all = await listAllEmployees(peopleGateway, { sortBy: 'name' });
        if (!cancelled) setEmployees(all);
      } catch {
        // Records stay visible; names fall back to a generic label.
      }
    })();
    return () => { cancelled = true; };
  }, [enabled]);
  return (id: string) => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.firstName} ${e.lastName} (${e.employeeNumber})` : 'EPN employee';
  };
}

/** "PartiallyApproved" becomes "Partially approved"; "INSUFFICIENT_BALANCE" becomes "Insufficient balance". Never raw, never invented. */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  const spaced = value.includes('_') || value === value.toUpperCase()
    ? value.replace(/_/g, ' ').toLowerCase()
    : value.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Whether the page has anything to show beyond the banner. A company with no connection and no records sees only the banner. */
export function hasEscoHrData(summary: EscoHrWorkforceSummaryResponse): boolean {
  return summary.status.entitlement !== 'Unknown' && (summary.status.connection !== null || summary.domain.records > 0);
}
